import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "border border-line bg-slate-100/70 px-3 py-2 text-sm text-slate-900 outline-none focus:border-blaze";

export const BookingSettings = () => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newTime, setNewTime] = useState("");

  useEffect(() => {
    axios.get(`${API}/settings/booking`).then(({ data }) => setS(data)).catch(() => toast.error("Could not load booking settings."));
  }, []);

  if (!s) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-blaze" /></div>;

  const setTruck = (i, k, v) => setS({ ...s, trucks: s.trucks.map((t, x) => (x === i ? { ...t, [k]: v } : t)) });
  const setExtra = (i, k, v) => setS({ ...s, extra_services: s.extra_services.map((e, x) => (x === i ? { ...e, [k]: v } : e)) });
  const addTime = () => {
    const t = newTime.trim().toUpperCase();
    if (!t || s.start_times.includes(t)) return;
    setS({ ...s, start_times: [...s.start_times, t] });
    setNewTime("");
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/booking`, {
        trucks: s.trucks.map((t) => ({ ...t, rate: parseFloat(t.rate) || 0, movers: parseInt(t.movers) || 1, capacity_m3: parseFloat(t.capacity_m3) || 0 })),
        extra_services: s.extra_services.map((e) => ({ ...e, price: parseFloat(e.price) || 0 })),
        start_times: s.start_times,
        box_price: parseFloat(s.box_price) || 0,
        stairs_rate: parseFloat(s.stairs_rate) || 0,
      });
      toast.success("Booking settings saved — live on the quote/booking wizard immediately.");
    } catch {
      toast.error("Could not save settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-6 space-y-6" data-testid="booking-settings">
      <div className="border border-line bg-white p-6">
        <p className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-blaze">Trucks & Crew Rates</p>
        <div className="space-y-5">
          {s.trucks.map((t, i) => (
            <div key={t.id} className="border border-line/60 p-4" data-testid={`settings-truck-${t.id}`}>
              <div className="grid gap-3 sm:grid-cols-2">
                <input className={inputCls} value={t.name} onChange={(e) => setTruck(i, "name", e.target.value)} placeholder="Truck name" />
                <div className="flex items-center gap-2"><span className="text-xs text-slate-500">Capacity m³</span><input type="number" className={`${inputCls} w-full`} value={t.capacity_m3} onChange={(e) => setTruck(i, "capacity_m3", e.target.value)} /></div>
              </div>
              <div className="mt-3 space-y-2">
                {(t.tiers || []).map((tier, ti) => (
                  <div key={tier.movers} className="flex items-center gap-2 text-xs" data-testid={`settings-tier-${t.id}-${tier.movers}`}>
                    <span className="w-16 font-semibold text-slate-600">{tier.movers} {tier.movers === 1 ? "man" : "men"}</span>
                    <span className="text-slate-400">$</span>
                    <input
                      type="number"
                      className={`${inputCls} w-24`}
                      value={tier.rate}
                      onChange={(e) => {
                        const trucks = s.trucks.map((tr, x) => {
                          if (x !== i) return tr;
                          const tiers = tr.tiers.map((te, y) => (y === ti ? { ...te, rate: e.target.value } : te));
                          const twoMen = tiers.find((te) => te.movers === 2) || tiers[0];
                          return { ...tr, tiers, rate: parseFloat(twoMen.rate) || 0, movers: twoMen.movers };
                        });
                        setS({ ...s, trucks });
                      }}
                      data-testid={`settings-tier-rate-${t.id}-${tier.movers}`}
                    />
                    <span className="text-slate-400">{tier.unit === "30min" ? "per 30 mins" : "per hour"}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <p className="mt-3 text-[11px] text-slate-400">1-man tiers are billed per 30 minutes and only offered for single-item moves. Call-out is charged at the same hourly rate as the chosen truck.</p>
      </div>

      <div className="border border-line bg-white p-6">
        <p className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-blaze">Available Start Times</p>
        <div className="flex flex-wrap gap-2" data-testid="settings-times">
          {s.start_times.map((t) => (
            <span key={t} className="flex items-center gap-1.5 border border-line bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700">
              {t}
              <button onClick={() => setS({ ...s, start_times: s.start_times.filter((x) => x !== t) })} className="text-slate-400 hover:text-red-600" data-testid={`settings-time-del-${t.replace(/[:\s]/g, "-")}`} aria-label={`Remove ${t}`}><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
        <div className="mt-3 flex gap-2">
          <input className={`${inputCls} w-40`} placeholder="e.g. 4:00 PM" value={newTime} onChange={(e) => setNewTime(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTime()} data-testid="settings-time-new" />
          <button onClick={addTime} className="flex items-center gap-1.5 border border-line px-4 py-2 text-xs font-bold uppercase tracking-widest text-slate-600 hover:border-blaze hover:text-blaze" data-testid="settings-time-add"><Plus className="h-3.5 w-3.5" /> Add Time</button>
        </div>
      </div>

      <div className="border border-line bg-white p-6">
        <p className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-blaze">Add-on Rates (Boxes & Stairs)</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Line Box price $</span>
            <input type="number" step="0.5" className={`${inputCls} w-full`} value={s.box_price} onChange={(e) => setS({ ...s, box_price: e.target.value })} data-testid="settings-box-price" />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Per flight of stairs $</span>
            <input type="number" step="1" className={`${inputCls} w-full`} value={s.stairs_rate} onChange={(e) => setS({ ...s, stairs_rate: e.target.value })} data-testid="settings-stairs-rate" />
          </div>
        </div>
        <p className="mt-3 text-[11px] text-slate-400">Customers pick quantities in the booking wizard; totals update automatically.</p>
      </div>

      <div className="border border-line bg-white p-6">
        <p className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-blaze">Extra Services & Prices</p>
        <div className="space-y-3">
          {s.extra_services.map((e, i) => (
            <div key={e.id} className="grid gap-3 sm:grid-cols-2" data-testid={`settings-extra-${e.id}`}>
              <input className={inputCls} value={e.label} onChange={(ev) => setExtra(i, "label", ev.target.value)} />
              <div className="flex items-center gap-2"><span className="text-xs text-slate-500">Price $</span><input type="number" className={`${inputCls} w-full`} value={e.price} onChange={(ev) => setExtra(i, "price", ev.target.value)} data-testid={`settings-extra-price-${e.id}`} /></div>
            </div>
          ))}
        </div>
        <p className="mt-3 text-[11px] text-slate-400">Price $0 shows as no extra charge on the wizard.</p>
      </div>

      <button onClick={save} disabled={saving} className="flex items-center gap-2 bg-blaze px-8 py-3 text-xs font-bold uppercase tracking-widest text-white disabled:opacity-50" data-testid="settings-save">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Settings"}
      </button>
    </div>
  );
};
